import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'react-bootstrap-icons';
import { apiUrl } from '../../config/api';
import './SearchBox.scss';

const DEBOUNCE_MS = 280;

const SearchBox = ({ autoFocus = false, onNavigate }) => {
  const navigate = useNavigate();
  const listboxId = useId();

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Debounced, abortable suggestion lookup. The previous version fired
  // one request per keystroke with no cancellation, so a slow early
  // response could overwrite the results of a later one.
  useEffect(() => {
    const trimmed = query.trim();

    if (!trimmed) {
      setSuggestions([]);
      setIsOpen(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          apiUrl(`/api/products/suggestion/${encodeURIComponent(trimmed)}`),
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        const items = Array.isArray(data.products) ? data.products : [];
        setSuggestions(items);
        setIsOpen(items.length > 0);
        setActiveIndex(-1);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setSuggestions([]);
          setIsOpen(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Close the suggestion list when focus or the pointer leaves.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const runSearch = useCallback(
    (rawQuery) => {
      const trimmed = (rawQuery || '').trim();
      setQuery('');
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      onNavigate?.();
      navigate(trimmed ? `/search/${encodeURIComponent(trimmed)}` : '/allproducts');
    },
    [navigate, onNavigate]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch(activeIndex >= 0 ? suggestions[activeIndex]?.product_name : query);
  };

  const handleKeyDown = (event) => {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="searchbox" ref={containerRef}>
      <form className="searchbox__form" onSubmit={handleSubmit} role="search">
        <label className="visually-hidden" htmlFor={`${listboxId}-input`}>
          Search jewellery
        </label>
        <Search className="searchbox__icon" size={17} aria-hidden="true" />
        <input
          id={`${listboxId}-input`}
          ref={inputRef}
          className="searchbox__input"
          type="search"
          placeholder="Search for necklaces, earrings, sets…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        />
        {query && (
          <button
            type="button"
            className="searchbox__clear"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <X size={17} aria-hidden="true" />
          </button>
        )}
        <button type="submit" className="searchbox__submit" aria-label="Search">
          <Search size={17} aria-hidden="true" />
        </button>
      </form>

      {isOpen && suggestions.length > 0 && (
        <ul className="searchbox__suggestions" id={listboxId} role="listbox">
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.product_code || suggestion.product_name}-${index}`}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`searchbox__suggestion${index === activeIndex ? ' is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              // `mousedown` fires before the input's blur, so the click
              // is not swallowed by the close-on-blur handler.
              onMouseDown={(event) => {
                event.preventDefault();
                runSearch(suggestion.product_name);
              }}
            >
              <Search size={14} aria-hidden="true" />
              <span>{suggestion.product_name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
